import React, { useState, useRef } from 'react';
import { Send, Lock, CreditCard, Wallet, Paperclip, X as XIcon, FileText as FileIcon } from 'lucide-react';
import { submitAssignment, uploadQuoteFile, supabase } from '../supabase';

const NOTIFY_EMAIL = 'arkexpertresearchers@gmail.com';

const paymentMethods = [
  { id:'card',   Icon:CreditCard, label:'Credit / Debit Card', sub:'Visa, Mastercard, Amex', color:'#0F52BA' },
  { id:'paypal', Icon:Wallet,     label:'PayPal',              sub:'Secure PayPal checkout', color:'#0891B2' },
];

const academicLevelMap = {
  'High School':               'high_school',
  'Undergraduate (Year 1–2)':  'undergraduate_1_2',
  'Undergraduate (Year 3–4)':  'undergraduate_3_4',
  'Postgraduate / Masters':    'postgraduate',
  'PhD / Doctoral':            'phd',
  'Professional / Other':      'professional',
};

const REFERENCING_STYLES = ['APA','Harvard','MLA','Chicago','Vancouver','AGLC','IEEE','OSCOLA','Other'];

const initialForm = {
  full_name:'', email:'', phone:'', country:'Malaysia',
  subject:'Accounting', academic_level:'Undergraduate (Year 1–2)',
  deadline_date:'', deadline_time:'23:59',
  word_count:'', assignment_type:'Essay',
  referencing:'APA', requirements:'',
};

// Email via mailto fallback — also triggers EmailJS if configured
async function sendNotificationEmail(formData, trackingId) {
  try {
    // Try EmailJS if keys are set in env
    const serviceId  = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (serviceId && templateId && publicKey) {
      const emailjs = await import('@emailjs/browser').catch(() => null);
      if (emailjs) {
        await emailjs.default.send(serviceId, templateId, {
          to_email:       NOTIFY_EMAIL,
          tracking_id:    trackingId,
          client_name:    formData.full_name,
          client_email:   formData.email,
          client_phone:   formData.phone,
          country:        formData.country,
          subject:        formData.subject,
          academic_level: formData.academic_level,
          assignment_type:formData.assignment_type,
          word_count:     formData.word_count || 'Not specified',
          deadline:       `${formData.deadline_date} at ${formData.deadline_time}`,
          referencing:    formData.referencing,
          payment:        formData.preferredPayment,
          requirements:   formData.requirements || 'None',
        }, publicKey);
        return;
      }
    }

    // Fallback: log to console so admin can see it in Vercel logs
    console.log('=== NEW QUOTE REQUEST ===', {
      trackingId,
      notify: NOTIFY_EMAIL,
      ...formData,
    });
  } catch (e) {
    console.warn('Email notification failed (non-fatal):', e.message);
  }
}

function FieldError({ msg }) {
  if (!msg) return null;
  return <div style={{fontSize:11,color:'#fca5a5',marginTop:4,fontWeight:600}}>⚠ {msg}</div>;
}

// ── File Uploader Component ──────────────────────────────────────────────────
const ACCEPTED = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.zip';
const MAX_SIZE_MB = 10;
const MAX_FILES = 5;

function FileUploader({ files, setFiles }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (incoming) => {
    const valid = Array.from(incoming).filter(f => {
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        alert(`"${f.name}" is too large (max ${MAX_SIZE_MB}MB)`);
        return false;
      }
      return true;
    });
    setFiles(prev => {
      const merged = [...prev, ...valid];
      if (merged.length > MAX_FILES) {
        alert(`Maximum ${MAX_FILES} files allowed.`);
        return merged.slice(0, MAX_FILES);
      }
      return merged;
    });
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_,i) => i !== idx));

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024*1024) return (bytes/1024).toFixed(1) + ' KB';
    return (bytes/(1024*1024)).toFixed(1) + ' MB';
  };

  return (
    <div>
      {/* Drop zone */}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        style={{
          border: `2px dashed ${dragging ? '#FF7A00' : 'rgba(255,255,255,0.2)'}`,
          borderRadius: 12,
          padding: '28px 20px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragging ? 'rgba(255,122,0,0.07)' : 'rgba(255,255,255,0.03)',
          transition: 'all 0.2s',
        }}
      >
        <Paperclip size={28} color={dragging ? '#FF7A00' : 'rgba(255,255,255,0.35)'} style={{marginBottom:10}}/>
        <div style={{fontSize:14,fontWeight:600,color:'rgba(255,255,255,0.75)',marginBottom:4}}>
          Drag & drop files here, or <span style={{color:'#FF7A00',textDecoration:'underline'}}>browse</span>
        </div>
        <div style={{fontSize:12,color:'rgba(255,255,255,0.35)'}}>
          PDF, Word, Excel, PowerPoint, Images, ZIP · Max {MAX_SIZE_MB}MB each · Up to {MAX_FILES} files
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED}
          style={{display:'none'}}
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
        />
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div style={{marginTop:12,display:'flex',flexDirection:'column',gap:8}}>
          {files.map((f, i) => (
            <div key={i} style={{
              display:'flex',alignItems:'center',gap:10,
              background:'rgba(255,255,255,0.06)',
              border:'1px solid rgba(255,255,255,0.12)',
              borderRadius:9,padding:'10px 14px',
            }}>
              <FileIcon size={18} color='#FF7A00' style={{flexShrink:0}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:'#fff',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.name}</div>
                <div style={{fontSize:11,color:'rgba(255,255,255,0.4)'}}>{formatSize(f.size)}</div>
              </div>
              <button
                onClick={() => removeFile(i)}
                style={{background:'rgba(220,38,38,0.15)',border:'none',borderRadius:6,padding:'4px 6px',cursor:'pointer',color:'#fca5a5',display:'flex',flexShrink:0}}>
                <XIcon size={14}/>
              </button>
            </div>
          ))}
          <div style={{fontSize:12,color:'rgba(255,255,255,0.35)',textAlign:'right'}}>
            {files.length}/{MAX_FILES} files · Note: files are listed in your submission. Our team may request you share them via WhatsApp or email if needed.
          </div>
        </div>
      )}
    </div>
  );
}

export default function QuotePage() {
  const [form, setForm]       = useState(initialForm);
  const [payment, setPayment] = useState('card');
  const [files, setFiles]     = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const set = (k) => (e) => {
    setError('');
    setFieldErrors(fe => ({ ...fe, [k]: '' }));
    setForm(f => ({ ...f, [k]: e.target.value }));
  };

  const validate = () => {
    const fe = {};
    if (!form.full_name.trim())  fe.full_name  = 'Full name is required';
    if (!form.email.trim())      fe.email      = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
                                 fe.email      = 'Please enter a valid email address';
    if (!form.phone.trim())      fe.phone      = 'Phone number is required';
    else if (form.phone.trim().replace(/[\s\-\+\(\)]/g,'').length < 7)
                                 fe.phone      = 'Please enter a valid phone number';
    if (!form.deadline_date)     fe.deadline_date = 'Deadline date is required';
    return fe;
  };

  const handleSubmit = async () => {
    setError('');
    const fe = validate();
    if (Object.keys(fe).length) { setFieldErrors(fe); return; }
    setFieldErrors({});

    const deadlineISO = new Date(`${form.deadline_date}T${form.deadline_time || '23:59'}:00`).toISOString();
    const preferredPayment = paymentMethods.find(p => p.id === payment)?.label || payment;

    setSaving(true);
    try {
      const payload = {
        subject:         form.subject,
        academic_level:  academicLevelMap[form.academic_level] || 'other',
        assignment_type: form.assignment_type,
        word_count:      form.word_count ? parseInt(form.word_count, 10) : null,
        referencing:     form.referencing,
        deadline:        deadlineISO,
        requirements:    form.requirements,
        internal_notes:
          `Name: ${form.full_name}\n` +
          `Email: ${form.email}\n` +
          `Phone: ${form.phone}\n` +
          `Country: ${form.country}\n` +
          `Preferred Payment: ${preferredPayment}` +
          (files.length ? `\nAttached Files: ${files.map(f=>f.name).join(', ')}` : ''),
        priority: 'normal',
        currency: 'USD',
      };

      const data = await submitAssignment(payload);

      // Upload attached files to Supabase Storage
      if (files.length > 0) {
        const uploadErrors = [];
        const uploadedPaths = [];

        for (const file of files) {
          try {
            const path = await uploadQuoteFile(file, data.tracking_id);
            uploadedPaths.push(path);
          } catch (uploadErr) {
            console.error(`Upload failed for ${file.name}:`, uploadErr);
            uploadErrors.push(`${file.name}: ${uploadErr.message}`);
          }
        }

        // Save whichever paths succeeded
        if (uploadedPaths.length > 0) {
          const { error: updateErr } = await supabase
            .from('assignments')
            .update({
              internal_notes: payload.internal_notes + `\nFile Paths: ${uploadedPaths.join('|')}`
            })
            .eq('tracking_id', data.tracking_id);

          if (updateErr) {
            console.error('Could not save file paths to order:', updateErr);
          }
        }

        // Warn if some files failed (non-blocking — order still submitted)
        if (uploadErrors.length > 0 && uploadedPaths.length === 0) {
          // All uploads failed — show a non-fatal warning
          setSubmitted(data.tracking_id);
          setForm(initialForm);
          setFiles([]);
          setError(`Note: Your order was submitted but file upload failed (${uploadErrors[0]}). Please send your files via WhatsApp: +1 (618) 815-6526`);
          setSaving(false);
          sendNotificationEmail({ ...form, preferredPayment }, data.tracking_id);
          return;
        }
      }

      // Send email notification (non-blocking)
      sendNotificationEmail({ ...form, preferredPayment }, data.tracking_id);

      setSubmitted(data.tracking_id);
      setForm(initialForm);
      setFiles([]);
    } catch (e) {
      console.error('Quote submission error:', e);
      const msg  = (e.message || '').toLowerCase();
      const code = e.code || '';
      const isRLS = msg.includes('row-level security') || msg.includes('permission denied') ||
                    msg.includes('policy') || code === '42501' || code === '42P17';
      setError(isRLS
        ? `Submission blocked by database security rules. Please run supabase_fix_guest_submit.sql and try again.`
        : `Something went wrong: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Success screen ──
  if (submitted) {
    return (
      <div className="quote-page" style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',textAlign:'center',padding:'40px 20px'}}>
        <div style={{maxWidth:480}}>
          <div style={{width:80,height:80,borderRadius:'50%',background:'rgba(22,163,74,0.15)',border:'2px solid #16a34a',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 24px',fontSize:36}}>✅</div>
          <h2 style={{fontFamily:'Sora,sans-serif',fontSize:'1.9rem',fontWeight:800,color:'#fff',marginBottom:16,lineHeight:1.3}}>
            Quote request submitted successfully.
          </h2>
          <div style={{background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.15)',borderRadius:14,padding:'20px 24px',marginBottom:24}}>
            <p style={{color:'rgba(255,255,255,0.6)',fontSize:14,marginBottom:8}}>Your tracking code is:</p>
            <div style={{fontFamily:'Sora,sans-serif',fontSize:'1.6rem',fontWeight:800,color:'var(--orange)',letterSpacing:2}}>{submitted}</div>
          </div>
          <p style={{color:'rgba(255,255,255,0.55)',fontSize:14,marginBottom:28,lineHeight:1.7}}>
            Our team will contact you as soon as possible.
          </p>
          <button className="btn-primary" onClick={() => setSubmitted(false)}>Submit Another Request</button>
        </div>
      </div>
    );
  }

  const inp = (extra={}) => ({
    style:{
      width:'100%', background:'rgba(255,255,255,0.06)', border:'1.5px solid rgba(255,255,255,0.14)',
      borderRadius:9, padding:'11px 14px', color:'#fff', fontSize:14,
      fontFamily:'Inter,sans-serif', outline:'none', boxSizing:'border-box',
      ...extra,
    }
  });

  return (
    <div className="quote-page">
      <div style={{textAlign:'center',marginBottom:44}}>
        <div className="section-label" style={{color:'var(--orange)',justifyContent:'center',display:'flex'}}>Free Quote</div>
        <h1 style={{fontFamily:'Sora,sans-serif',fontSize:'clamp(1.8rem,3vw,2.4rem)',fontWeight:800,color:'#fff',marginBottom:10}}>Get Your Free Quote</h1>
        <p style={{color:'rgba(255,255,255,0.58)',fontSize:15}}>Fill in your details and receive a quote within 30 minutes</p>
      </div>

      <div className="quote-form-wrap">
        <div className="quote-form-grid">

          {/* Full Name */}
          <div className="qfg">
            <label>Full Name *</label>
            <input type="text" placeholder="Your full name" value={form.full_name} onChange={set('full_name')} {...inp(fieldErrors.full_name?{borderColor:'#ef4444'}:{})}/>
            <FieldError msg={fieldErrors.full_name}/>
          </div>

          {/* Email — REQUIRED */}
          <div className="qfg">
            <label>Email Address <span style={{color:'#ef4444'}}>*</span></label>
            <input type="email" placeholder="your@email.com" value={form.email} onChange={set('email')} {...inp(fieldErrors.email?{borderColor:'#ef4444'}:{})}/>
            <FieldError msg={fieldErrors.email}/>
          </div>

          {/* Phone — REQUIRED */}
          <div className="qfg">
            <label>Phone / WhatsApp <span style={{color:'#ef4444'}}>*</span></label>
            <input type="tel" placeholder="+1 (618) 815-6526" value={form.phone} onChange={set('phone')} {...inp(fieldErrors.phone?{borderColor:'#ef4444'}:{})}/>
            <FieldError msg={fieldErrors.phone}/>
          </div>

          {/* Country */}
          <div className="qfg">
            <label>Country</label>
            <select value={form.country} onChange={set('country')} className="qfg-select">
              {['Malaysia','Singapore','Hong Kong','Indonesia','Thailand','Philippines','China','Japan','South Korea','Vietnam','United Arab Emirates','Qatar','Saudi Arabia','Kuwait','Bahrain','Oman','United States','Canada','Australia','United Kingdom','Kenya','Nigeria','Other'].map(c=><option key={c}>{c}</option>)}
            </select>
          </div>

          {/* Subject */}
          <div className="qfg">
            <label>Subject / Field *</label>
            <select value={form.subject} onChange={set('subject')} className="qfg-select">
              {['Accounting','Agriculture & Agribusiness','Architecture','Biology & Life Sciences','Business & Management','Chemistry','Computer Science & IT','Criminology','Data Science','Economics','Education','Engineering','English Literature','Environmental Science','Finance & Banking','Geography','Healthcare Management','Hospitality & Tourism','HRM','Law','Marketing','Mathematics & Statistics','Medicine','Nursing','Philosophy & Ethics','Political Science','Psychology','Public Health','Sociology & Social Work','Other'].map(c=><option key={c}>{c}</option>)}
            </select>
          </div>

          {/* Academic Level */}
          <div className="qfg">
            <label>Academic Level *</label>
            <select value={form.academic_level} onChange={set('academic_level')} className="qfg-select">
              {Object.keys(academicLevelMap).map(c=><option key={c}>{c}</option>)}
            </select>
          </div>

          {/* Deadline Date */}
          <div className="qfg">
            <label>Deadline Date *</label>
            <input type="date" value={form.deadline_date} onChange={set('deadline_date')} {...inp(fieldErrors.deadline_date?{borderColor:'#ef4444'}:{})}/>
            <FieldError msg={fieldErrors.deadline_date}/>
          </div>

          {/* Deadline Time */}
          <div className="qfg">
            <label>Deadline Time</label>
            <input type="time" value={form.deadline_time} onChange={set('deadline_time')} {...inp()}/>
          </div>

          {/* Word Count */}
          <div className="qfg">
            <label>Word Count</label>
            <input type="number" placeholder="e.g. 2500" value={form.word_count} onChange={set('word_count')} {...inp()}/>
          </div>

          {/* Assignment Type */}
          <div className="qfg">
            <label>Assignment Type</label>
            <select value={form.assignment_type} onChange={set('assignment_type')} className="qfg-select">
              {['Essay','Research Paper','Dissertation','Coursework','Case Study','Literature Review','Presentation','Thesis','Research Proposal','Data Analysis','Proofreading / Editing','Lab Report','Other'].map(c=><option key={c}>{c}</option>)}
            </select>
          </div>

          {/* Referencing — APA to 7th removed */}
          <div className="qfg">
            <label>Referencing Style</label>
            <select value={form.referencing} onChange={set('referencing')} className="qfg-select">
              {REFERENCING_STYLES.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>

          {/* Additional Instructions — full width */}
          <div className="qfg full">
            <label>Additional Instructions</label>
            <textarea
              placeholder="Describe your requirements, learning objectives, marking rubric, or any special instructions..."
              value={form.requirements}
              onChange={set('requirements')}
              style={{minHeight:120,resize:'vertical'}}
            />
          </div>

          {/* File Attachments */}
          <div className="qfg full">
            <label style={{display:'block',fontSize:13,fontWeight:600,color:'rgba(255,255,255,0.72)',marginBottom:8}}>
              Attach Files <span style={{fontSize:11,fontWeight:400,color:'rgba(255,255,255,0.4)'}}>— Optional (rubric, instructions, previous work, etc.)</span>
            </label>
            <FileUploader files={files} setFiles={setFiles}/>
          </div>

          {/* Payment Method */}
          <div className="qfg full">
            <label style={{marginBottom:12,display:'block',fontSize:13,fontWeight:600,color:'rgba(255,255,255,0.72)'}}>
              Preferred Payment Method
            </label>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:10}}>
              {paymentMethods.map(({id,Icon,label,sub,color}) => (
                <button key={id} type="button" onClick={() => setPayment(id)} style={{
                  display:'flex',alignItems:'center',gap:10,padding:'12px 14px',borderRadius:10,
                  cursor:'pointer',transition:'all 0.2s',textAlign:'left',fontFamily:'Inter,sans-serif',
                  border: payment===id ? `2px solid ${color}` : '1.5px solid rgba(255,255,255,0.12)',
                  background: payment===id ? `${color}22` : 'rgba(255,255,255,0.05)',
                }}>
                  <div style={{width:36,height:36,borderRadius:9,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.2s',background:payment===id?`${color}30`:'rgba(255,255,255,0.08)',color:payment===id?color:'rgba(255,255,255,0.5)'}}>
                    <Icon size={18}/>
                  </div>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:payment===id?'#fff':'rgba(255,255,255,0.7)',lineHeight:1.2}}>{label}</div>
                    <div style={{fontSize:11,color:'rgba(255,255,255,0.4)',marginTop:2}}>{sub}</div>
                  </div>
                  {payment===id && <div style={{marginLeft:'auto',width:16,height:16,borderRadius:'50%',background:color,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:10,color:'#fff',fontWeight:700}}>✓</div>}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div style={{marginTop:20,background:'rgba(220,38,38,0.12)',border:'1px solid rgba(220,38,38,0.3)',borderRadius:10,padding:'12px 16px',color:'#fca5a5',fontSize:13,textAlign:'center'}}>
            ⚠️ {error}
          </div>
        )}

        <div style={{textAlign:'center',marginTop:28}}>
          <button className="btn-primary" style={{padding:'15px 48px',fontSize:16,opacity:saving?0.7:1}} onClick={handleSubmit} disabled={saving}>
            {saving ? 'Submitting…' : <><Send size={17}/> Get My Free Quote</>}
          </button>
          <p style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6,fontSize:12,color:'rgba(255,255,255,0.35)',marginTop:14}}>
            <Lock size={13}/> 100% confidential — your details are never shared with third parties
          </p>
        </div>
      </div>
    </div>
  );
}
